/**
 * DEMO MODE (rev 12) — bypass the login screen for UI review while the real
 * identity stack is unfinished.
 *
 * WHY THIS EXISTS
 *   The deployed frontend cannot authenticate: `apps/web/src/lib/auth.ts`
 *   POSTs to `/api/auth/login` (an Express route that is not deployed), the
 *   Supabase Edge Function requires a `tenant_id` the frontend never sends,
 *   and Cloudflare Pages serves static files with an SPA fallback for every
 *   path. The result is a login screen that can never succeed, which blocks
 *   all UI review.
 *
 * WHY IT IS NOT "REMOVING THE LOGIN"
 *   Deleting the guard, or accepting any token, would undo the rev9–rev11
 *   security work and would create a permanent backdoor. This module instead
 *   does the narrow, honest thing:
 *
 *     • It seeds a LOCAL, clearly-labelled demo session so the UI renders.
 *     • It does NOT change the API. The server still requires a signed JWT and
 *       still denies by default. Demo mode can never become a server bypass,
 *       because it mints nothing the server accepts.
 *     • It is OFF unless `VITE_DEMO_MODE=1` is set at build time.
 *     • It is impossible to confuse with a real login: the user, tenant and
 *       banner are visibly synthetic.
 *
 * The consequence is stated plainly: in demo mode the app renders, but data
 * calls still fail closed, because the API is not deployed. That is the
 * correct outcome — a demo session must not become a way to read real records.
 */

/** Storage key shared with `lib/route-auth.ts`. */
const SESSION_KEY = "dikdoc_user";

/** Banner state, so the shell can render an unambiguous warning. */
const DEMO_FLAG_KEY = "dikdoc_demo_active";

/** Read at BUILD time by Vite. Absent or "0" => demo mode is off. */
export function isDemoMode(): boolean {
  const raw = import.meta.env?.VITE_DEMO_MODE;
  return String(raw ?? "").trim() === "1";
}

/** Minimal SHA-256 over a UTF-8 string, using WebCrypto. */
async function sha256Hex(input: string): Promise<string> {
  const data = new TextEncoder().encode(input);
  const digest = await crypto.subtle.digest("SHA-256", data);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export const DEMO_USER = {
  id: "demo-superadmin",
  email: "admin@medigroup.ma",
  name: "Demo (synthetic)",
  roles: ["admin", "doctor"],
  tenantId: "demo-tenant",
  tenantName: "Demo — données synthétiques",
} as const;

/**
 * Install the demo session if demo mode is on and no real session exists.
 *
 * A REAL session is never overwritten: if a genuine login has already stored
 * one, it is left alone.
 */
export function installDemoSession(): boolean {
  if (!isDemoMode()) return false;
  try {
    if (window.localStorage.getItem(SESSION_KEY)) return false; // real session wins
    window.localStorage.setItem(SESSION_KEY, JSON.stringify(DEMO_USER));
    window.localStorage.setItem(DEMO_FLAG_KEY, "1");
    // No token is written. Deliberately: the API must keep denying.
    return true;
  } catch {
    return false;
  }
}

export function isDemoSession(): boolean {
  try {
    return window.localStorage.getItem(DEMO_FLAG_KEY) === "1";
  } catch {
    return false;
  }
}

/** Remove the demo session, restoring the login screen. */
export function clearDemoSession(): void {
  try {
    if (isDemoSession()) window.localStorage.removeItem(SESSION_KEY);
    window.localStorage.removeItem(DEMO_FLAG_KEY);
  } catch {
    /* storage unavailable */
  }
}
